import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label } from 'reactstrap'
import { getIdData } from '../../lib/studentinfo'
import { getFile } from '../../lib/file'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

// Default avatar image
const emptyAvatar = require('../../assets/images/user/user-dp.png')

const StudentInfoModal = ({ isOpen, toggle, studentId }) => {
	const [studentInfo, setStudentInfo] = useState(null)
	const [avatar, setAvatar] = useState(null)
	const [loading, setLoading] = useState(false)
	const navigate = useNavigate()
	const layout = useLayout()
	const role = useRoleBasedUrl()

	const fetchStudentData = async () => {
		if (!studentId) return

		try {
			setLoading(true)
			const std = await getIdData(studentId)
			setStudentInfo(std[0])

			if (std[0]?.avatar_url) {
				const avt = await getFile(std[0].avatar_url)
				setAvatar(avt.publicUrl)
			}
		} catch (error) {
			console.error('Error fetching student data:', error)
		} finally {
			setLoading(false)
		}
	}

	const handleUpdate = () => {
		navigate(`${process.env.PUBLIC_URL}/${role}/class-management/student-update/${studentId}/${layout}`)
		toggle()
	}

	useEffect(() => {
		if (isOpen && studentId) {
			fetchStudentData()
		}
	}, [isOpen, studentId])

	if (!studentInfo) {
		return null
	}

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
			<ModalHeader toggle={toggle}>
				<h5 className="mb-0">Student Information</h5>
			</ModalHeader>
			<ModalBody>
				{loading ? (
					<div className="text-center py-4">
						<div className="spinner-border text-primary" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
						<p className="mt-2">Loading student data...</p>
					</div>
				) : (
					<>
						{/* Student Header Info */}
						<div className="row mb-4">
							<div className="col-3">
								<div className="student-img text-center">
									<img
										className="rounded-circle"
										style={{ height: '100px', width: '100px', objectFit: 'cover' }}
										alt="Student Avatar"
										src={avatar || emptyAvatar}
									/>
								</div>
							</div>
							<div className="col-9">
								<h3 className="mb-2">{studentInfo.name ? `${studentInfo.name} ${studentInfo.surname || ''}`.trim() : 'Student Name Not Found'}</h3>
								<p className="text-muted mb-1">
									{studentInfo.schoolname ? `${studentInfo.schoolname} - ${studentInfo.class || ''}` : 'School Information Not Found'}
								</p>
								{studentInfo.email && (
									<p className="text-muted mb-0">
										<i className="fa fa-envelope me-2"></i>
										{studentInfo.email}
									</p>
								)}
							</div>
						</div>

						{/* Student Details */}
						<div className="row">
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-control-label fw-bold">Graduation Year</Label>
									<p className="break-line">{studentInfo.graduateyear || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-control-label fw-bold">GPA</Label>
									<p className="break-line">{studentInfo.gpa || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-control-label fw-bold">SAT / PSAT</Label>
									<p className="break-line">{studentInfo.satpsat || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-control-label fw-bold">Practice SAT</Label>
									<p className="break-line">{studentInfo.statetest || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-control-label fw-bold">Major</Label>
									<p className="break-line">{studentInfo.major || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">Career</Label>
									<p className="break-line">{studentInfo.career || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">Five Adjectives</Label>
									<p className="break-line">{studentInfo.fiveAdjectives || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">About my High School</Label>
									<p className="break-line">{studentInfo.hsSchoolStatistics || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">Top Three Colleges Choices</Label>
									<p className="break-line">{studentInfo.topThreeColleges || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">Letter of Recommendation</Label>
									<p className="break-line">{studentInfo.lor || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">Activities</Label>
									<p className="break-line">{studentInfo.activities || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">College Essay</Label>
									<p className="break-line">{studentInfo.collegeEssay || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">Counselor's Comment</Label>
									<p className="break-line">{studentInfo.instructorNote || 'N/A'}</p>
								</div>
							</div>
							<div className="col-lg-4 col-md-6 mb-3">
								<div className="form-group">
									<Label className="form-label fw-bold">Student's Comment</Label>
									<p className="break-line">{studentInfo.studentNote || 'N/A'}</p>
								</div>
							</div>
						</div>
					</>
				)}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" onClick={toggle}>
					Close
				</Button>
				<Button color="primary" onClick={handleUpdate}>
					Update Student
				</Button>
			</ModalFooter>
		</Modal>
	)
}

export { StudentInfoModal }
